import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NoiseOverlay } from "@/components/NoiseOverlay";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PushOptIn } from "@/components/PushOptIn";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ScanReport from "@/pages/ScanReport";
import NegativeItems from "@/pages/NegativeItems";
import Disputes from "@/pages/Disputes";
import DisputeDetail from "@/pages/DisputeDetail";
import Tips from "@/pages/Tips";
import ReportReview from "@/pages/ReportReview";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MortgageSim from "@/pages/MortgageSim";
import CfpbEscalation from "@/pages/CfpbEscalation";
import IdTheftKit from "@/pages/IdTheftKit";
import TradeLines from "@/pages/TradeLines";
import AdminTradeLines from "@/pages/AdminTradeLines";
import MailPage from "@/pages/MailPage";
import CreditGuide from "@/pages/CreditGuide";
import Disclaimer from "@/pages/Disclaimer";
import Terms from "@/pages/Terms";
import GetReport from "@/pages/GetReport";
import Refer from "@/pages/Refer";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0C0E]">
        <div className="font-display text-2xl text-[#F5F3EE]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <NoiseOverlay />
      <BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          toastOptions={{
            style: { fontFamily: "IBM Plex Sans, sans-serif" },
          }}
        />
        <InstallPrompt />
        <PushOptIn />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/playbook" element={<CreditGuide />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Disclaimer />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app/scan" element={<ProtectedRoute><ScanReport /></ProtectedRoute>} />
          <Route path="/app/report" element={<ProtectedRoute><ReportReview /></ProtectedRoute>} />
          <Route path="/app/items" element={<ProtectedRoute><NegativeItems /></ProtectedRoute>} />
          <Route path="/app/disputes" element={<ProtectedRoute><Disputes /></ProtectedRoute>} />
          <Route path="/app/disputes/:id" element={<ProtectedRoute><DisputeDetail /></ProtectedRoute>} />
          <Route path="/app/tips" element={<ProtectedRoute><Tips /></ProtectedRoute>} />
          <Route path="/app/mortgage-sim" element={<ProtectedRoute><MortgageSim /></ProtectedRoute>} />
          <Route path="/app/cfpb" element={<ProtectedRoute><CfpbEscalation /></ProtectedRoute>} />
          <Route path="/app/idtheft" element={<ProtectedRoute><IdTheftKit /></ProtectedRoute>} />
          <Route path="/app/tradelines" element={<ProtectedRoute><TradeLines /></ProtectedRoute>} />
          <Route path="/app/mail" element={<ProtectedRoute><MailPage /></ProtectedRoute>} />
          <Route path="/app/guide" element={<ProtectedRoute><CreditGuide /></ProtectedRoute>} />
          <Route path="/app/get-report" element={<ProtectedRoute><GetReport /></ProtectedRoute>} />
          <Route path="/app/refer" element={<ProtectedRoute><Refer /></ProtectedRoute>} />
          <Route path="/app/disclaimer" element={<ProtectedRoute><Disclaimer /></ProtectedRoute>} />
          <Route path="/app/terms" element={<ProtectedRoute><Terms /></ProtectedRoute>} />
          <Route path="/app/admin/tradelines" element={<ProtectedRoute><AdminTradeLines /></ProtectedRoute>} />
          <Route path="/app/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
          <Route path="/app/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
